import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Loader2, LayoutTemplate, Copy, Database, FilePlus2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import api, { apiError } from "../../lib/api";
import { slugify } from "../../lib/deckModel";

const TYPES = ["Residential", "Commercial", "Mixed Use", "Public-Private"];
const STARTS = [
  ["template", "Create from Template", "ARQITEK Investment / JV Deck — Murrieta layout, 14 sections", LayoutTemplate],
  ["duplicate", "Duplicate Existing Deck", "Copy structure, sections and exits — data resets", Copy],
  ["import", "Import Project Data", "Pull property data from an ARQITEK record", Database],
  ["blank", "Start Blank", "An empty deck you build section by section", FilePlus2],
];
const inp = "w-full rounded-md border border-border bg-secondary/30 px-3 py-2 text-sm outline-none focus:border-primary";

export const uploadDeckImage = async (file) => {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await api.post("/decks/images", fd);
  return data.url;
};

export function CreateDeckDialog({ open, onOpenChange, decks }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [p, setP] = useState({ project_type: "Commercial" });
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugState, setSlugState] = useState(null);
  const [start, setStart] = useState("template");
  const [sourceId, setSourceId] = useState("");
  const [copyData, setCopyData] = useState(false);
  const [recordQ, setRecordQ] = useState("");
  const [records, setRecords] = useState([]);
  const [recordId, setRecordId] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  useEffect(() => { if (!slugEdited) setSlug(slugify(p.name)); }, [p.name, slugEdited]);
  useEffect(() => {
    if (!slug) { setSlugState(null); return; }
    const t = setTimeout(() => api.get(`/decks/slug-check?slug=${encodeURIComponent(slug)}`).then((r) => setSlugState(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [slug]);
  useEffect(() => {
    if (start !== "import" || recordQ.length < 2) return;
    const t = setTimeout(() => api.get(`/analysis?search=${encodeURIComponent(recordQ)}&limit=8`).then((r) => setRecords(r.data.items || [])).catch(() => setRecords([])), 300);
    return () => clearTimeout(t);
  }, [recordQ, start]);

  const onCover = async (f) => {
    if (!f) return;
    try { set("cover_image", await uploadDeckImage(f)); toast.success("Cover image uploaded"); } catch (e) { toast.error(apiError(e)); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/decks", { project: p, slug, start, source_deck_id: sourceId || undefined, copy_data: copyData, record_id: recordId || undefined });
      toast.success("Deck created"); onOpenChange(false); navigate(`/decks/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const F = (k, l, ph) => (
    <div className="space-y-1"><label className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</label>
      <input value={p[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} className={inp} data-testid={`create-deck-${k}`} /></div>
  );
  const step1Ok = p.name && slug && slugState?.available !== false;
  const step2Ok = start !== "duplicate" || sourceId;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setStep(1); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-deck-dialog">
        <DialogHeader>
          <DialogTitle>Create Deck <span className="text-muted-foreground font-normal text-sm">· Step {step} of 2 — {step === 1 ? "Project" : "Starting Point"}</span></DialogTitle>
          <DialogDescription>{step === 1 ? "Tell us about the project. The public URL is generated from the name." : "Choose how this deck should start."}</DialogDescription>
        </DialogHeader>
        {step === 1 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">{F("name", "Project name", "e.g. Menifee Public Market")}</div>
            <div className="sm:col-span-2 space-y-1">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Public URL</label>
              <div className="flex items-center rounded-md border border-border bg-secondary/30 pl-3 text-sm">
                <span className="text-muted-foreground">arqitek.co/</span>
                <input value={slug} onChange={(e) => { setSlugEdited(true); setSlug(slugify(e.target.value)); }} className="flex-1 bg-transparent px-1 py-2 outline-none font-mono" data-testid="create-deck-slug" />
              </div>
              {slugState && !slugState.available && <p className="text-xs text-destructive" data-testid="create-deck-slug-error">{slugState.reason === "reserved" ? "That URL is reserved by the ARQITEK app." : "That URL is already used by another deck."}</p>}
            </div>
            <div className="space-y-1"><label className="text-[11px] uppercase tracking-wide text-muted-foreground">Project type</label>
              <select value={p.project_type} onChange={(e) => set("project_type", e.target.value)} className={inp} data-testid="create-deck-project_type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
            {F("stage", "Project stage", "e.g. Entitlement")}
            {F("address", "Address")}{F("city", "City")}{F("state", "State")}{F("county", "County")}
            {F("apn", "APN")}{F("owner", "Property owner")}{F("acreage", "Acreage")}
            <div className="space-y-1"><label className="text-[11px] uppercase tracking-wide text-muted-foreground">Cover image</label>
              <label className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm cursor-pointer hover:bg-secondary/40" data-testid="create-deck-cover">
                <Upload className="h-4 w-4" />{p.cover_image ? "Replace image" : "Upload image"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onCover(e.target.files?.[0])} />
              </label></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {STARTS.map(([k, l, d, Icon]) => (
                <button key={k} type="button" onClick={() => setStart(k)} data-testid={`create-deck-start-${k}`}
                  className={`text-left rounded-lg border p-4 transition-colors ${start === k ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/40"}`}>
                  <Icon className={`h-5 w-5 mb-2 ${start === k ? "text-primary" : "text-muted-foreground"}`} />
                  <div className="text-sm font-medium">{l}</div><div className="text-xs text-muted-foreground mt-0.5">{d}</div>
                </button>
              ))}
            </div>
            {start === "duplicate" && (
              <div className="space-y-2">
                <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} className={inp} data-testid="create-deck-source">
                  <option value="">Choose a deck to duplicate…</option>
                  {decks.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={copyData} onChange={(e) => setCopyData(e.target.checked)} data-testid="create-deck-copy-data" />Also copy project data, assumptions and section text</label>
              </div>
            )}
            {start === "import" && (
              <div className="space-y-2">
                <input value={recordQ} onChange={(e) => setRecordQ(e.target.value)} placeholder="Search Property Analysis records…" className={inp} data-testid="create-deck-record-search" />
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {records.map((r) => (
                    <button key={r.id} type="button" onClick={() => setRecordId(r.id)} className={`w-full text-left rounded-md border px-3 py-2 text-sm ${recordId === r.id ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`create-deck-record-${r.id}`}>
                      {r.property_analysis_name || r.property_name || r.first_name} <span className="text-muted-foreground">· {[r.city, r.state].filter(Boolean).join(", ")}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {step === 2 && <Button variant="ghost" onClick={() => setStep(1)} data-testid="create-deck-back">Back</Button>}
          {step === 1
            ? <Button onClick={() => setStep(2)} disabled={!step1Ok} data-testid="create-deck-next">Next</Button>
            : <Button onClick={create} disabled={busy || !step2Ok} data-testid="create-deck-submit">{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Create deck</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
